/**
 * API service for points-related API calls
 */

import { getApiBaseUrl } from "./apiBase.js";

/**
 * Get the authentication token from storage
 * @returns {string|null} The authentication token or null if not found
 */
const getAuthToken = () => {
  return (
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token")
  );
};

/**
 * Get points for the currently logged in user
 * @returns {Promise<Object>} The user's points data
 */
export const getMyPoints = async () => {
  const token = getAuthToken();

  if (!token) {
    throw new Error("No authentication token found. Please login.");
  }

  try {
    const response = await fetch(`${getApiBaseUrl()}/points/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem("access_token");
        sessionStorage.removeItem("access_token");
        throw new Error("Session expired. Please login again.");
      }
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching user points:", error);
    throw error;
  }
};

/**
 * Get points for a specific student
 * @param {string} studentId The student ID
 * @returns {Promise<Object>} The student's points data
 */
export const getStudentPoints = async (studentId) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error("No authentication token found. Please login.");
  }

  try {
    const response = await fetch(
      `${getApiBaseUrl()}/points/student/${studentId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem("access_token");
        sessionStorage.removeItem("access_token");
        throw new Error("Session expired. Please login again.");
      }
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching student points:", error);
    throw error;
  }
};

/**
 * Get points leaderboard
 * @param {number} limit Number of entries to return
 * @param {number} skip Number of entries to skip
 * @returns {Promise<Object>} The leaderboard data
 */
export const getPointsLeaderboard = async (limit = 10, skip = 0) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error("No authentication token found. Please login.");
  }

  try {
    const response = await fetch(
      `${getApiBaseUrl()}/points/leaderboard?limit=${limit}&skip=${skip}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem("access_token");
        sessionStorage.removeItem("access_token");
        throw new Error("Session expired. Please login again.");
      }
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching points leaderboard:", error);
    throw error;
  }
};

/**
 * Get points history for a user
 * @param {string} userId The user ID
 * @param {number} days Number of days to include in the history
 * @returns {Promise<Object>} The points history data
 */
export const getPointsHistory = async (userId, days = 30) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error("No authentication token found. Please login.");
  }

  try {
    const response = await fetch(
      `${getApiBaseUrl()}/points/history/${userId}?days=${days}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem("access_token");
        sessionStorage.removeItem("access_token");
        throw new Error("Session expired. Please login again.");
      }
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching points history:", error);
    throw error;
  }
};
